import { z } from 'zod';
/**
 * Tickets and the messy desk (DESIGN.md §10).
 *
 * ## What a ticket carries, and what it deliberately does not
 *
 * The snapshot — title, seat, date — is the night as it was, frozen at issue
 * (§4). The back is the memory. What is **not** here is `back_state`: stubs and
 * hand-written backs are meant to be indistinguishable ("nobody but you can
 * tell which is which"), and a field on the wire is a field anybody can read in
 * the network tab. The host's editor will get its own shape.
 *
 * ## Desk coordinates are fractions of the desk
 *
 * Same reasoning as seats being fractions of the room image (`seats.ts`), and a
 * stronger case: the desk *is* the viewport, so it is 1920 wide on one visit
 * and 390 on the next. `x`/`y` are the ticket's centre in `[0, 1]`, and the
 * client clamps on the way in, because a ticket at x = 0.98 on a desktop is
 * partly through the right-hand wall of a phone.
 */
export const deskPlacementSchema = z.object({
    /** Centre, as a fraction of the desk's width. */
    x: z.number().min(0).max(1),
    /** Centre, as a fraction of the desk's height. */
    y: z.number().min(0).max(1),
    /**
     * Degrees clockwise. 360 is accepted and stored as 0 — rounding a thrown
     * ticket's angle to a tenth of a degree lands on it more often than you'd
     * think, and refusing the whole save over it would be absurd.
     */
    rotation: z.number().min(0).max(360),
    /** Stacking rank; higher is on top. Resting tickets may overlap. */
    z: z.number().int().min(0),
});
export const ticketSchema = z.object({
    id: z.string().uuid(),
    /** Null once the screening is deleted — tickets outlive their sources. */
    screeningId: z.string().uuid().nullable(),
    movieTitle: z.string(),
    seatLabel: z.string(),
    /** ISO timestamp of the night. */
    screenedOn: z.string(),
    backText: z.string().nullable(),
    backDoodleUrl: z.string().nullable(),
    /** Null means never placed: the client throws it onto the desk. */
    desk: deskPlacementSchema.nullable(),
});
/** Your own tickets, oldest night first. */
export const ticketsResponse = z.object({
    tickets: z.array(ticketSchema),
});
/**
 * Not a technical limit. A weekly movie night is fifty tickets a year; this is
 * a decade of them, and a request past it is a bug, not a collection.
 */
export const MAX_DESK_PLACEMENTS = 500;
/**
 * Where some of your tickets now lie.
 *
 * A **subset**, not the whole desk — unlike the seat map, which saves whole.
 * A ticket issued while the desk was open is not in the client's list, and a
 * whole-desk save would have to decide what its absence means. Here absence
 * means "not moved", which is always true.
 */
export const putDeskRequest = z.object({
    placements: z
        .array(deskPlacementSchema.extend({ id: z.string().uuid() }))
        .min(1)
        .max(MAX_DESK_PLACEMENTS),
});
export const putDeskResponse = z.object({
    /** How many of the placements named one of *your* tickets. */
    saved: z.number().int(),
});
//# sourceMappingURL=tickets.js.map